import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { api } from '../../lib/api';
import { useStore } from '../../context/StoreContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import * as FiIcons from 'react-icons/fi';

const { FiUploadCloud, FiAlertCircle, FiCheckCircle, FiChevronRight, FiUsers, FiUserCheck, FiUserPlus, FiAlertTriangle } = FiIcons;

const TARGET_FIELDS = [
  { id: 'first_name', label: 'First Name (or Full Name)', required: true },
  { id: 'last_name', label: 'Last Name (Optional if using Full Name)', required: false },
  { id: 'email', label: 'Email', required: false },
  { id: 'phone', label: 'Phone', required: false },
  { id: 'wedding_date', label: 'Wedding Date', required: false },
  { id: 'rsvp_date', label: 'RSVP Date', required: false },
  { id: 'rsvp_source', label: 'RSVP Source', required: false },
  { id: 'partner_name', label: 'Partner Name', required: false },
  { id: 'notes', label: 'Notes', required: false }
];

export const Imports = () => {
  const navigate = useNavigate();
  const { currentEvent, staffName } = useStore();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [detectedFormat, setDetectedFormat] = useState(null);
  const [mapping, setMapping] = useState({});
  const [summary, setSummary] = useState(null);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, stage: '' });
  const [importError, setImportError] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) processFile(selectedFile);
    e.target.value = null;
  };

  const processFile = (file) => {
    setError(null);
    const extension = file.name.split('.').pop().toLowerCase();
    
    if (extension !== 'csv' && file.type !== 'text/csv' && file.type !== 'application/vnd.ms-excel') {
      setError('Invalid file format. Please upload a CSV file.');
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (!results.data || results.data.length === 0) {
          setError('The CSV file is empty.');
          return;
        }
        setFile(file);
        
        // Clean up headers and attach a sample of the data to help the user identify columns
        const headers = results.meta.fields.map((h, i) => {
          const clean = h.replace(/^[\uFEFF\u200B]+/, '').trim();
          return {
            id: clean || `Column ${i + 1}`,
            original: h,
            sample: results.data[0] ? results.data[0][h] : ''
          };
        });

        setCsvHeaders(headers);
        
        // Re-map data with clean headers
        const cleanData = results.data.map(row => {
          const cleanRow = {};
          headers.forEach(h => {
            cleanRow[h.id] = row[h.original];
          });
          return cleanRow;
        });
        
        setCsvData(cleanData);
        autoMapFields(headers);
        setStep('mapping');
      },
      error: (err) => setError(`Error parsing CSV: ${err.message}`)
    });
  };

  const autoMapFields = (headers) => {
    const newMapping = {};
    let format = 'Fillout / Standard';
    
    if (headers.some(h => h.id.includes('Order No.') || h.id.includes('Ticket Class'))) format = 'Eventbrite';
    setDetectedFormat(format);

    TARGET_FIELDS.forEach(field => {
      let match = headers.find(h => h.id.toLowerCase() === field.label.toLowerCase() || h.id.toLowerCase() === field.id.replace('_', ' '));
      
      if (!match) {
        if (field.id === 'first_name') {
          match = headers.find(h => {
            const lower = h.id.toLowerCase();
            return lower.includes('first name') || lower === 'fname' || lower === 'name' || lower === 'full name' || lower.includes('buyer first name') || lower.includes('what is your name');
          });
        }
        if (field.id === 'last_name') {
          match = headers.find(h => {
            const lower = h.id.toLowerCase();
            return lower.includes('last name') || lower === 'lname' || lower.includes('buyer last name');
          });
        }
        if (field.id === 'email') {
          match = headers.find(h => h.id.toLowerCase().includes('email'));
        }
        if (field.id === 'phone') {
          match = headers.find(h => h.id.toLowerCase().includes('phone') || h.id.toLowerCase().includes('cell') || h.id.toLowerCase().includes('mobile'));
        }
        if (field.id === 'wedding_date') {
          match = headers.find(h => h.id.toLowerCase().includes('wedding date') || h.id.toLowerCase().includes('date of wedding'));
        }
      }
      if (match) newMapping[field.id] = match.id;
    });

    setMapping(newMapping);
  };

  const handlePrepareReview = async () => {
    setError(null);
    if (!mapping.first_name) {
      setError('You must map at least the First Name (or Full Name) column to proceed.');
      return;
    }

    try {
      const existingContacts = await api.getAllContacts();
      let newCount = 0, existCount = 0, errCount = 0;

      csvData.forEach((row) => {
        let fname = mapping.first_name ? row[mapping.first_name] : '';
        let lname = mapping.last_name ? row[mapping.last_name] : '';

        // Auto-split full name if they only mapped a single "Name" column
        if (fname && !lname && !mapping.last_name) {
          const parts = fname.trim().split(/\s+/);
          if (parts.length > 1) {
            fname = parts[0];
            lname = parts.slice(1).join(' ');
          }
        }

        if (!fname && !lname) {
          errCount++;
          return;
        }

        const email = mapping.email ? row[mapping.email] : '';
        const phone = mapping.phone ? row[mapping.phone] : '';

        const normEmail = email?.trim().toLowerCase();
        const normPhone = phone?.replace(/\D/g, '');

        const exists = existingContacts.find(c => 
          (normEmail && c.email?.trim().toLowerCase() === normEmail) || 
          (normPhone && c.phone?.replace(/\D/g, '') === normPhone)
        );

        if (exists) existCount++;
        else newCount++;
      });

      setSummary({ total: csvData.length, newCount, existCount, errCount });
      setStep('review');
    } catch (err) {
      console.error("Review prep error:", err);
      setError(err.message || 'Error preparing review. Please try again.');
    }
  };

  const handleConfirmImport = async () => {
    if (step === 'importing') return;
    setStep('importing');
    setImportError(null);
    setImportProgress({ current: 0, total: csvData.length, stage: 'Initializing local import...' });

    try {
      let eventToUse = currentEvent || await api.getOrCreateMidsummerEvent();
      
      const formattedData = csvData.map(row => {
        const mappedRow = {};
        
        let fname = mapping.first_name ? row[mapping.first_name] : '';
        let lname = mapping.last_name ? row[mapping.last_name] : '';

        // Auto-split full name for the actual import as well
        if (fname && !lname && !mapping.last_name) {
          const parts = fname.trim().split(/\s+/);
          if (parts.length > 1) {
            fname = parts[0];
            lname = parts.slice(1).join(' ');
          }
        }

        mappedRow.first_name = fname;
        mappedRow.last_name = lname;

        TARGET_FIELDS.forEach(field => {
          if (field.id !== 'first_name' && field.id !== 'last_name' && mapping[field.id]) {
            mappedRow[field.id] = row[mapping[field.id]];
          }
        });
        
        return mappedRow;
      });

      const stats = await api.importBatch(
        eventToUse.event_id,
        formattedData,
        staffName,
        file.name,
        detectedFormat,
        (progress) => setImportProgress(progress)
      );

      setImportResult(stats);
      setStep('success');
    } catch (err) {
      setImportError(err.message || 'An unexpected error occurred during the local import.');
      setStep('error');
    }
  };

  if (step === 'success') {
    return (
      <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in zoom-in py-12">
        <div className="text-center">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FiCheckCircle className="text-green-600 text-5xl" />
          </div>
          <h1 className="text-3xl font-cinzel text-padua-green">Import Complete</h1>
          <p className="text-gray-500 text-lg mt-2"><strong>{importResult.total_rows} rows processed locally</strong></p>
        </div>

        <Card className="p-8 mt-8">
          <ul className="space-y-4 text-gray-700 text-lg">
            <li className="flex justify-between border-b pb-2">
              <span>New Contacts</span>
              <span className="font-bold text-padua-green">{importResult.new_contacts}</span>
            </li>
            <li className="flex justify-between border-b pb-2">
              <span>Existing Contacts Updated</span>
              <span className="font-bold text-padua-gold">{importResult.existing_contacts}</span>
            </li>
            <li className="flex justify-between border-b pb-2">
              <span>Rows Skipped</span>
              <span className="font-bold text-gray-400">{importResult.skipped_rows}</span>
            </li>
          </ul>
        </Card>

        <div className="pt-8 flex gap-4 justify-center">
          <Button variant="outline" onClick={() => setStep('upload')}>IMPORT ANOTHER FILE</Button>
          <Button variant="primary" onClick={() => navigate('/admin/guests')}>VIEW GUEST DIRECTORY</Button>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in py-12 text-center">
        <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <FiAlertTriangle className="text-red-600 text-5xl" />
        </div>
        <h1 className="text-3xl font-cinzel text-red-700">Import Failed</h1>
        
        <Card className="p-6 mt-6 bg-red-50 border-red-100 text-left">
          <p className="text-sm font-bold text-red-800 uppercase tracking-wider mb-2">Error Details</p>
          <p className="text-red-700 mb-4 font-medium">{importError}</p>
        </Card>

        <div className="pt-8 flex gap-4 justify-center">
          <Button variant="outline" onClick={() => setStep('review')}>RETURN TO REVIEW</Button>
          <Button variant="primary" onClick={handleConfirmImport}>TRY AGAIN</Button>
        </div>
      </div>
    );
  }

  if (step === 'importing') {
    const percentage = importProgress.total > 0 ? Math.round((importProgress.current / importProgress.total) * 100) : 0;
    
    return (
      <div className="max-w-2xl mx-auto text-center py-20 animate-in fade-in">
        <h1 className="text-3xl font-cinzel text-padua-green mb-4">IMPORTING GUESTS</h1>
        <p className="text-lg text-gray-500 font-medium">Processing {importProgress.current} of {importProgress.total}</p>
        
        <div className="w-full bg-gray-200 rounded-full h-4 mt-8 mb-4 overflow-hidden">
          <div className="bg-padua-gold h-4 rounded-full transition-all duration-300 ease-out" style={{ width: `${percentage}%` }}></div>
        </div>
        <p className="text-sm text-gray-400 uppercase tracking-wider font-semibold">{importProgress.stage}</p>
      </div>
    );
  }

  if (step === 'review') {
    return (
      <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in">
        <div>
          <h1 className="text-2xl font-cinzel text-padua-green">IMPORT REVIEW</h1>
          <p className="text-gray-500 mt-1">Review data before saving to iPad local storage.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-6 text-center">
            <FiUsers className="text-3xl text-blue-500 mx-auto mb-3" />
            <div className="text-3xl font-bold text-gray-900">{summary.total}</div>
            <div className="text-sm text-gray-500 uppercase tracking-wider mt-1">Total Rows</div>
          </Card>
          <Card className="p-6 text-center">
            <FiUserPlus className="text-3xl text-green-500 mx-auto mb-3" />
            <div className="text-3xl font-bold text-gray-900">{summary.newCount}</div>
            <div className="text-sm text-gray-500 uppercase tracking-wider mt-1">New Contacts</div>
          </Card>
          <Card className="p-6 text-center">
            <FiUserCheck className="text-3xl text-padua-gold mx-auto mb-3" />
            <div className="text-3xl font-bold text-gray-900">{summary.existCount}</div>
            <div className="text-sm text-gray-500 uppercase tracking-wider mt-1">Existing</div>
          </Card>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-xl flex items-center">
            <FiAlertCircle className="mr-2 text-xl flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-4 pt-4">
          <Button variant="outline" onClick={() => setStep('mapping')}>Back</Button>
          <Button variant="primary" onClick={handleConfirmImport}>CONFIRM IMPORT</Button>
        </div>
      </div>
    );
  }

  if (step === 'mapping') {
    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in pb-12">
        <div className="flex justify-between items-end">
          <div>
            <h1 className="text-2xl font-cinzel text-padua-green">Map CSV Columns</h1>
            <p className="text-gray-500 mt-1">File: <span className="font-medium text-gray-900">{file.name}</span></p>
          </div>
          {detectedFormat && (
            <div className="bg-padua-green/10 text-padua-green px-4 py-2 rounded-full text-sm font-medium">
              {detectedFormat} Source
            </div>
          )}
        </div>
        
        <div className="bg-blue-50 text-blue-800 p-4 rounded-xl text-sm border border-blue-100 flex gap-3">
          <FiAlertCircle className="text-lg flex-shrink-0 mt-0.5" />
          <p>
            The dropdown menus show the <strong>columns from your uploaded file</strong> along with a preview of the data (e.g. "John"). 
            Match your columns to the Padua fields on the left. If your file only has one "Name" column, map it to First Name and we will split it automatically.
          </p>
        </div>

        <Card className="p-0 overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="p-4 font-medium text-gray-500">Padua Field</th>
                <th className="p-4 font-medium text-gray-500">Your CSV Column</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {TARGET_FIELDS.map(field => {
                const isRequiredMissing = field.required && !mapping[field.id];
                return (
                  <tr key={field.id} className={`hover:bg-gray-50/50 ${isRequiredMissing ? 'bg-red-50/30' : ''}`}>
                    <td className="p-4">
                      <div className="font-medium text-gray-900 flex items-center">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </div>
                    </td>
                    <td className="p-4">
                      <select 
                        className={`w-full max-w-sm p-3 border rounded-lg focus:ring-padua-gold bg-white transition-colors ${
                          isRequiredMissing ? 'border-red-500 bg-red-50 ring-1 ring-red-500' : 'border-gray-300'
                        }`}
                        value={mapping[field.id] || ''}
                        onChange={e => {
                          setError(null);
                          setMapping({...mapping, [field.id]: e.target.value});
                        }}
                      >
                        <option value="">-- Ignore Field --</option>
                        {csvHeaders.map((h, i) => (
                          <option key={i} value={h.id}>
                            {h.id} {h.sample ? `(e.g., ${String(h.sample).substring(0, 25)})` : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>

        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-xl flex items-center mt-4 border border-red-200 shadow-sm">
            <FiAlertCircle className="mr-2 text-2xl flex-shrink-0" />
            <span className="font-medium">{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-4 pt-4">
          <Button variant="outline" onClick={() => setStep('upload')}>Cancel</Button>
          <Button variant="primary" onClick={handlePrepareReview} className="flex items-center">
            Review Import <FiChevronRight className="ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in">
      <div>
        <h1 className="text-2xl font-cinzel text-padua-green">Import Guests</h1>
        <p className="text-gray-500 mt-1">Upload CSV files from Eventbrite or Fillout directly to local iPad storage.</p>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-xl flex items-center">
          <FiAlertCircle className="mr-2 text-xl flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <input 
        type="file" 
        accept=".csv,text/csv" 
        className="hidden" 
        ref={fileInputRef} 
        onChange={handleFileSelect} 
      />

      <Card 
        className={`p-12 border-2 border-dashed transition-colors cursor-pointer text-center ${isDragging ? 'border-padua-gold bg-padua-gold/5' : 'border-gray-300 hover:bg-gray-50'}`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
        onDrop={(e) => { 
          e.preventDefault(); 
          setIsDragging(false); 
          const f = e.dataTransfer.files[0];
          if(f) processFile(f);
        }}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="w-16 h-16 bg-padua-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <FiUploadCloud className="text-3xl text-padua-green" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Click or drag CSV file to upload</h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
          The system will automatically map fields and merge duplicates based on email and phone.
        </p>
        <Button variant="primary" onClick={(e) => {
          e.stopPropagation();
          fileInputRef.current?.click();
        }}>
          SELECT CSV FILE
        </Button>
      </Card>
    </div>
  );
};